import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { FaPlus, FaMinus, FaUsers, FaHandHoldingUsd, FaCalculator } from 'react-icons/fa';

import PageHeader from '../../components/misc/PageHeader';
import useSelectedBusiness from '../../../hooks/business/useSelectedBusiness';
import * as bankAccountService from '../../../services/bankAccountService';
import { ProfitAndLoss } from '../../../types/bankAccount';
import { formatEuroCentsWithSign } from '../../../utils/bankAccountUtil';

interface FinancialProfitLossPageProps {
  hideHeader?: boolean;
}

interface ProfitLossData {
  groupIncome: number;
  groupExpenses: number;
  contributionIncome: number;
  totalIncome: number;
  totalCosts: number;
  result: number;
}

export default function FinancialProfitLossPage({
  hideHeader = false,
}: FinancialProfitLossPageProps): JSX.Element {
  const { t, i18n } = useTranslation('', { keyPrefix: 'page.financial.reports.profitLoss' });
  const business = useSelectedBusiness();
  const [profitLossData, setProfitLossData] = React.useState<ProfitLossData | null>(null);

  useEffect(() => {
    if (business) {
      bankAccountService
        .getProfitAndLoss()
        .then((data) => {
          const processedData = processProfitLossData(data);
          setProfitLossData(processedData);
        })
        .catch(() => {
          setProfitLossData(null);
        });
    }
  }, [business]);

  const processProfitLossData = (data: ProfitAndLoss): ProfitLossData => {
    const contributionIncome = Math.abs(data.contributionExpenses);
    const totalIncome = data.groupIncome + contributionIncome;
    const totalCosts = Math.abs(data.groupExpenses);
    const result = data.groupIncome + data.groupExpenses + data.contributionExpenses;

    return {
      groupIncome: data.groupIncome,
      groupExpenses: data.groupExpenses,
      contributionIncome,
      totalIncome,
      totalCosts,
      result,
    };
  };

  const locale = i18n.language;

  if (!profitLossData) {
    return <div>Loading...</div>;
  }

  return (
    <>
      {!hideHeader && <PageHeader title={t('title')} subtitle={t('subtitle')} />}

      <div className="flex flex-1 flex-col gap-5 pb-4">
        <div className="rounded-xl bg-white p-6 text-gray-800 shadow-md">
          {/* Header */}
          <p className="mb-6 text-sm text-gray-500">
            {t('dateRange', {
              startDate: 'N/A',
              endDate: new Date().toLocaleDateString(),
            })}
          </p>

          {/* Main grid */}
          <div className="grid grid-cols-1 gap-8 lg:grid-cols-2">
            {/* Income */}
            <div>
              <div className="mb-4 flex flex-row items-center gap-2">
                <FaPlus className="text-success" />
                <h3 className="inline-block text-xl font-semibold text-success">
                  {t('income.title')}
                </h3>
              </div>

              {/* Group Income */}
              <div className="mb-4 rounded-lg border p-4">
                <div className="mb-3 flex flex-row items-center gap-2">
                  <FaUsers className="text-success" />
                  <h4 className="font-medium text-success">{t('income.groupIncome')}</h4>
                </div>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span>{t('income.groupIncome')}</span>
                    <span className="text-success">
                      {formatEuroCentsWithSign(profitLossData.groupIncome, locale)}
                    </span>
                  </div>
                </div>
              </div>

              {/* Contribution Income */}
              <div className="mb-4 rounded-lg border p-4">
                <div className="mb-3 flex flex-row items-center gap-2">
                  <FaHandHoldingUsd className="text-success" />
                  <h4 className="font-medium text-success">{t('income.contributionIncome')}</h4>
                </div>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span>{t('income.contributionIncome')}</span>
                    <span className="text-success">
                      {formatEuroCentsWithSign(profitLossData.contributionIncome, locale)}
                    </span>
                  </div>
                </div>
              </div>

              {/* Total Income */}
              <div className="flex justify-between rounded-lg border border-green-200 bg-green-50 p-4 font-semibold">
                <span className="text-success">{t('income.totalIncome')}</span>
                <span className="text-success">
                  {formatEuroCentsWithSign(profitLossData.totalIncome, locale)}
                </span>
              </div>
            </div>

            {/* Costs */}
            <div>
              <div className="mb-4 flex flex-row items-center gap-2">
                <FaMinus className="text-danger" />
                <h3 className="inline-block text-xl font-semibold text-danger">
                  {t('costs.title')}
                </h3>
              </div>

              {/* Group Expenses */}
              <div className="mb-4 rounded-lg border p-4">
                <div className="mb-3 flex flex-row items-center gap-2">
                  <FaCalculator className="text-danger" />
                  <h4 className="font-medium text-danger">{t('costs.groupCosts')}</h4>
                </div>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span>{t('costs.groupCosts')}</span>
                    <span className="text-danger">
                      {formatEuroCentsWithSign(profitLossData.groupExpenses, locale)}
                    </span>
                  </div>
                </div>
              </div>

              {/* Total Costs */}
              <div className="flex justify-between rounded-lg border border-red-200 bg-red-50 p-4 font-semibold">
                <span className="text-danger">{t('costs.totalCosts')}</span>
                <span className="text-danger">
                  {formatEuroCentsWithSign(-profitLossData.totalCosts, locale)}
                </span>
              </div>
            </div>
          </div>

          {/* Result */}
          <div className="mt-8 flex justify-between rounded-lg border-2 border-blue-200 bg-blue-50 p-6 font-bold">
            <div className="flex items-center gap-2">
              <span className="text-xl text-primary-300">{t('result.title')}</span>
            </div>
            <span
              className={`text-2xl ${profitLossData.result >= 0 ? 'text-success' : 'text-danger'}`}>
              {formatEuroCentsWithSign(profitLossData.result, locale)}
            </span>
          </div>
        </div>
      </div>
    </>
  );
}
